import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { api } from '../lib/api';
import { RefreshCw, AlertTriangle, BookOpen, Check, Info, X } from 'lucide-react';
import toast from 'react-hot-toast';

interface Task {
  AID: number;
  LPID: number;
  Kurzberzeichnung: string;
  Sortierung: number;
  Zeitbedarf?: number;
  Zusatzaufgabe?: number;
}

interface LearningPlan {
  LPID: number;
  NAME: string;
  FACH: string;
  START_DATUM: string;
  END_DATUM: string;
  BID: number;
}

interface TasksData {
  learningPlan: LearningPlan;
  tasks: Task[];
  completedTasks: number[];
}

interface LearningPlanTasksModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentUnid: string;
  learningPlan: any;
}

// Task Tile Component
interface TaskTileProps {
  task: Task;
  studentUnid: string;
  taskColor: string;
  isCompleted: boolean;
  onStatusChange: (aid: number, isCompleted: boolean) => void;
}

function TaskTile({ task, studentUnid, taskColor, isCompleted, onStatusChange }: TaskTileProps) {
  const [showModal, setShowModal] = useState(false);
  const [taskDetails, setTaskDetails] = useState<any>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const loadTaskDetails = async () => {
    setIsLoadingDetails(true);
    try {
      const response = await api.get(`/lernbuero/task-details/${task.AID}`);
      setTaskDetails(response.data.data);
    } catch (error) {
      console.error('Fehler beim Laden der Aufgaben-Details:', error);
      toast.error('Fehler beim Laden der Aufgaben-Details');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowModal(true);
    if (!taskDetails) {
      loadTaskDetails();
    }
  };

  const completeTaskMutation = useMutation(
    async () => {
      const response = await api.post(`/students/${studentUnid}/tasks/complete`, {
        aid: task.AID,
        lpid: task.LPID
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        onStatusChange(task.AID, true);
        toast.success('Aufgabe als erledigt markiert');
        const initialized = data?.skillmap?.initialized;
        if (initialized && initialized > 0) {
          toast.success(`Kompetenzmap aktualisiert (${initialized} neue Kompetenzen)`);
        }
        const evidenceCreated = data?.competencyEvidence?.created;
        if (evidenceCreated && evidenceCreated > 0) {
          toast.success(`Kompetenz-Nachweis gespeichert (${evidenceCreated})`);
        }
        // Refetch tasks to get updated completion status - will be handled by parent
      },
      onError: (error) => {
        console.error('Fehler beim Markieren der Aufgabe:', error);
        toast.error('Fehler beim Markieren der Aufgabe');
      }
    }
  );

  const uncompleteTaskMutation = useMutation(
    async () => {
      const response = await api.delete(`/students/${studentUnid}/tasks/complete`, {
        data: { aid: task.AID }
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        onStatusChange(task.AID, false);
        toast.success('Aufgabe als nicht erledigt markiert');
        const removedCompetencies = data?.competencyEvidenceRemoved;
        if (removedCompetencies && removedCompetencies > 0) {
          toast.success(`Kompetenz-Nachweise entfernt (${removedCompetencies})`);
        }
        // Refetch tasks to get updated completion status - will be handled by parent
      },
      onError: (error) => {
        console.error('Fehler beim Entfernen der Markierung:', error);
        toast.error('Fehler beim Entfernen der Markierung');
      }
    }
  );

  const handleTaskToggle = () => {
    if (isCompleted) {
      uncompleteTaskMutation.mutate();
    } else {
      completeTaskMutation.mutate();
    }
  };

  return (
    <div
      className="relative group"
      style={{ backgroundColor: isCompleted ? taskColor + '40' : '#e9ecef' }}
    >
      <button
        onClick={handleTaskToggle}
        className={`w-full h-20 p-2 rounded-lg border-2 transition-all duration-200 ${
          isCompleted
            ? 'border-green-500 bg-green-50'
            : 'border-gray-300 hover:border-gray-400 bg-gray-100'
        } ${isCompleted ? 'shadow-md' : 'hover:shadow-md'}`}
        disabled={completeTaskMutation.isLoading || uncompleteTaskMutation.isLoading}
      >
        <div className="h-full flex flex-col justify-center items-center relative">
          <div className="text-center">
            <p className={`text-xs font-medium ${
              isCompleted ? 'text-green-700 line-through' : 'text-gray-900'
            }`}>
              {task.Kurzberzeichnung}
            </p>
          </div>
          
          <div className="absolute bottom-1 right-1">
            {isCompleted ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <div className="w-4 h-4 border-2 border-gray-300 rounded" />
            )}
          </div>
        </div>
      </button>
      
      <div
        className="absolute top-1 left-1 w-4 h-4 rounded-full"
        style={{ backgroundColor: taskColor }}
      />
      
      <button
        onClick={handleInfoClick}
        className="absolute top-1 right-1 w-4 h-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold transition-colors"
        title="Aufgaben-Details anzeigen"
      >
        i
      </button>

      {/* Task Details Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Aufgaben-Details</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {isLoadingDetails ? (
              <div className="flex justify-center items-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-3 text-gray-600">Lade Details...</span>
              </div>
            ) : taskDetails ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">AID:</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{taskDetails.AID}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kurzbezeichnung:</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{taskDetails.Kurzberzeichnung}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aufgabenstellung:</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded whitespace-pre-wrap">{taskDetails.Aufgabenstellung || 'Keine Aufgabenstellung verfügbar'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link:</label>
                  {taskDetails.Link ? (
                    <a 
                      href={taskDetails.Link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline break-all"
                    >
                      {taskDetails.Link}
                    </a>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Kein Link verfügbar</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SForm:</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{taskDetails.SForm || 'Keine SForm verfügbar'}</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Keine Details verfügbar
              </div>
            )}
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Color list for learning plans
const colorList = [
  '#000000', '#0000FF', '#00FF00', '#FF0000', '#FFFF00',
  '#FF00FF', '#00FFFF', '#00FF00', '#800000', '#808000',
  '#000080', '#800080', '#008080', '#808080', '#C0C0C0', '#FFFFFF',
];

export default function LearningPlanTasksModal({ isOpen, onClose, studentUnid, learningPlan }: LearningPlanTasksModalProps) {
  const [taskProgress, setTaskProgress] = useState<Map<number, boolean>>(new Map());
  const queryClient = useQueryClient();

  const { data: tasksData, isLoading, error, refetch } = useQuery<TasksData>(
    ['learning-plan-tasks', studentUnid, learningPlan?.LPID],
    () => api.get(`/students/${studentUnid}/learning-plans/${learningPlan.LPID}/tasks`).then(res => res.data.data),
    {
      enabled: isOpen && !!studentUnid && !!learningPlan?.LPID,
      retry: 3,
      onError: (error) => {
        console.error('Fehler beim Laden der Aufgaben:', error);
        toast.error('Fehler beim Laden der Aufgaben');
      }
    }
  );

  // Update task progress from completed tasks
  useEffect(() => {
    if (!tasksData?.tasks || !tasksData?.completedTasks) return;

    const updatedProgress = new Map<number, boolean>();
    tasksData.tasks.forEach(task => {
      updatedProgress.set(task.AID, tasksData.completedTasks.includes(task.AID));
    });
    setTaskProgress(updatedProgress);
  }, [tasksData]);

  const getTaskColor = () => {
    // Use a fixed color for this learning plan
    return colorList[0]; // You can adjust this logic
  };

  const handleStatusChange = (aid: number, isCompleted: boolean) => {
    setTaskProgress(prev => {
      const next = new Map(prev);
      next.set(aid, isCompleted);
      return next;
    });
    // Refetch to get updated data
    refetch();
    queryClient.invalidateQueries(['learning-plan-tasks', studentUnid, learningPlan?.LPID]);
    // Also invalidate the learning plans list to reflect changes
    queryClient.invalidateQueries(['student-learning-plans', studentUnid]);
  };

  if (!isOpen) return null;

  const taskColor = getTaskColor();
  const taskList = tasksData?.tasks || [];
  const completedTasks = taskList.reduce((count, task) => {
    return count + (taskProgress.get(task.AID) ? 1 : 0);
  }, 0);
  const totalTasks = taskList.length;
  const progressPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Lernplan: {learningPlan?.NAME}</h2>
            <p className="text-sm text-gray-600 mt-1">
              Fach: {learningPlan?.FACH} | 
              Start: {learningPlan?.START_DATUM ? new Date(learningPlan.START_DATUM).toLocaleDateString('de-DE') : '-'} | 
              Ende: {learningPlan?.END_DATUM ? new Date(learningPlan.END_DATUM).toLocaleDateString('de-DE') : '-'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Lade Aufgaben...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Fehler beim Laden</h3>
            <p className="text-gray-500 mb-4">Die Aufgaben konnten nicht geladen werden.</p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Erneut versuchen
            </button>
          </div>
        ) : !tasksData || tasksData.tasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <BookOpen className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Aufgaben verfügbar</h3>
            <p className="text-gray-500 text-center">
              Für diesen Lernplan sind keine Aufgaben verfügbar.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Tasks Grid */}
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
              {tasksData.tasks.map((task) => {
                const taskIsCompleted = taskProgress.get(task.AID) || false;
                return (
                  <TaskTile
                    key={task.AID}
                    task={task}
                    studentUnid={studentUnid}
                    taskColor={taskColor}
                    isCompleted={taskIsCompleted}
                    onStatusChange={handleStatusChange}
                  />
                );
              })}
            </div>

            {/* Progress Bar */}
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="text-sm font-medium text-blue-900">
                  Fortschritt: {completedTasks}/{totalTasks} Aufgaben ({progressPercentage}%)
                </div>
                <div className="w-24 bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

